import React, { ReactNode } from 'react';
import {
    Card,
    CardBody,
    CardHeader,
    Step,
    StepIcon,
    StepIndicator,
    StepNumber,
    Stepper,
    StepSeparator,
    StepStatus,
    StepTitle,
} from '@chakra-ui/react';
import { DigitalOnboardingStatus } from '../../../generated/tenant-api';
import { UiKit } from '@datavant/uikit';
import { Button, Tooltip } from '@datavant/dart';
import { CustomIcons } from '../../CustomIcons';

export type DisplayStep = {
    title: ReactNode | string;
    subSteps?: SubStep[];
    onClick?: () => void;
    statuses?: DigitalOnboardingStatus[];
    tooltip?: string;
    pendoId?: string; // optional html attribute used for pendo tagging
};

type SubStep = {
    item: string;
    itemComplete: boolean;
    onClick?: () => void;
    pendoId?: string; // optional html attribute used for pendo tagging
    tags?: { component: JSX.Element; isActive: boolean }[];
};

type StepDisplayProps = {
    title: string;
    activeStep: number;
    steps: DisplayStep[];
    isSideNavOpen: boolean;
};

const getBorderStyle = (index: number, activeStep: number) => {
    const color = index < activeStep ? 'black' : index === activeStep ? '#006CCF' : '#E7E5DF';
    const fill = index < activeStep ? 'solid' : 'dotted';
    return `2px ${fill} ${color}`;
};

const SubStepDisplay = ({ item, itemComplete, onClick, tags, pendoId }: SubStep) => {
    return (
        <UiKit.Chakra.VStack align={'start'} spacing={0} width="100%">
            <UiKit.Chakra.HStack
                key={item}
                width={'100%'}
                onClick={() => {
                    if (onClick && !itemComplete) {
                        onClick();
                    }
                }}
                style={onClick && !itemComplete ? { cursor: 'pointer' } : undefined}
            >
                <UiKit.Icons.FontAwesomeIcon
                    icon={
                        itemComplete
                            ? UiKit.Icons.FontAwesome.regular.faCircleCheck
                            : UiKit.Icons.FontAwesome.regular.faCircle
                    }
                    color={itemComplete ? UiKit.consistency.dataGreen70 : UiKit.consistency.gray50}
                />
                <UiKit.Chakra.Stack width="100%">
                    <UiKit.Text.BodyRegular>{item}</UiKit.Text.BodyRegular>
                </UiKit.Chakra.Stack>
                {onClick && !itemComplete && (
                    <UiKit.Chakra.HStack>
                        <Button appearance={'solid'} bg={'blue.5'} size={'xs'} p={4} w={'24px'} h={'24px'}>
                            {CustomIcons.arrowRight({ fill: '#ffffff' })}
                        </Button>
                    </UiKit.Chakra.HStack>
                )}
            </UiKit.Chakra.HStack>
            <UiKit.Chakra.Stack paddingLeft={UiKit.consistency.buffer}>
                {tags?.map(tag => tag.isActive && tag.component)}
            </UiKit.Chakra.Stack>
        </UiKit.Chakra.VStack>
    );
};

export const StepDisplay = ({ title, activeStep, steps, isSideNavOpen }: StepDisplayProps) => {
    return isSideNavOpen ? (
        <Card maxWidth={'300px'} pt={isSideNavOpen ? '0px' : '20px'} data-testid="rra-tracker-step-display">
            {isSideNavOpen && (
                <CardHeader paddingBottom={'30px'}>
                    <UiKit.Text.Heading3 text={title} />
                </CardHeader>
            )}
            <CardBody pt={0} pr={isSideNavOpen ? '8px' : '4px'} pl={isSideNavOpen ? '8px' : '4px'}>
                <Stepper index={activeStep} orientation="vertical" height="100%" gap="0" size={'sm'}>
                    {steps.map((step, index) => {
                        return (
                            <Step key={index} style={{ width: '100%', paddingBottom: '10px' }}>
                                <StepIndicator
                                    sx={{
                                        '[data-status=complete] &': {
                                            background: 'black',
                                            borderColor: 'black',
                                        },
                                        '[data-status=active] &': {
                                            background: '#006CCF',
                                            borderColor: '#006CCF',
                                            color: 'white',
                                        },
                                    }}
                                >
                                    <StepStatus
                                        complete={<StepIcon />}
                                        incomplete={<StepNumber style={{ fontSize: '12px' }} />}
                                        active={<StepNumber style={{ fontSize: '12px' }} />}
                                    />
                                </StepIndicator>

                                <UiKit.Chakra.VStack spacing={0} alignItems="baseline" w="100%">
                                    {isSideNavOpen && (
                                        <StepTitle
                                            style={index === activeStep ? { width: '100%' } : undefined}
                                            onClick={index === activeStep ? step.onClick : undefined}
                                        >
                                            <UiKit.Chakra.HStack
                                                justifyContent="space-between"
                                                w="100%"
                                                alignItems="center"
                                                h="32px"
                                                paddingBottom={'8px'}
                                                fontSize={'16px'}
                                            >
                                                <span>{step.title}</span>
                                                {step.tooltip && (
                                                    <Tooltip
                                                        label={step.tooltip}
                                                        position="top"
                                                        multiline
                                                        w={220}
                                                        withArrow
                                                        withinPortal
                                                        zIndex={10000}
                                                    >
                                                        {CustomIcons.infoOutlined()}
                                                    </Tooltip>
                                                )}
                                                {step.onClick && index === activeStep && (
                                                    <Button
                                                        appearance="solid"
                                                        size="xs"
                                                        p={4}
                                                        w="24px"
                                                        h="24px"
                                                        data-pendo-id={step.pendoId}
                                                        onClick={step.onClick}
                                                    >
                                                        {CustomIcons.arrowRight({ fill: '#ffffff' })}
                                                    </Button>
                                                )}
                                            </UiKit.Chakra.HStack>
                                        </StepTitle>
                                    )}
                                    {index === activeStep &&
                                        isSideNavOpen &&
                                        step.subSteps?.map((subStep, subIndex) => (
                                            <SubStepDisplay key={subIndex} {...subStep} />
                                        ))}
                                </UiKit.Chakra.VStack>

                                <StepSeparator
                                    style={{
                                        top: 'calc(var(--stepper-indicator-size))',
                                        maxHeight: 'calc(100% - var(--stepper-indicator-size))',
                                        background: 'none',
                                        borderLeft: getBorderStyle(index, activeStep),
                                    }}
                                />
                            </Step>
                        );
                    })}
                </Stepper>
            </CardBody>
        </Card>
    ) : null;
};
